<template>
  <DropdownButton
    v-if="canAdd"
    ref="dropdownRef"
    class="create-item-button"
    size="small"
    variant="primary"
    outlined
    :show-arrow="false"
    :align="$attrs['is-right-aligned'] ? 'end' : 'start'"
    @open="
      () => {
        openedDropdownSection = (currentSection && currentSection.key) || null
      }
    "
  >
    <template #trigger>
      <!-- need to open menu when payload or item is not creatable -->
      <slot name="trigger">
        <div class="trigger-content field has-addons">
          <p v-if="showOneClickCreate" class="control">
            <BaseButton
              size="small"
              variant="primary"
              outlined
              :icon="['fa-solid', 'fa-plus']"
              :label="`New ${newItemLabel}`"
              @click.stop="
                $router.push({
                  path: `/${appName}/${modelName}/${modelType}/new`,
                })
              "
            />
          </p>
          <p
            v-if="
              !showOneClickCreate || (showOneClickCreate && showBatchImport)
            "
            class="control multi-click-create"
          >
            <BaseButton
              v-if="showOneClickCreate && showBatchImport"
              size="small"
              variant="primary"
              outlined
            >
              <template #right-icon>
                <i class="fa-solid fa-chevron-down" />
              </template>
            </BaseButton>
            <BaseButton
              v-else
              size="small"
              variant="primary"
              outlined
              :icon="['fa-solid', 'fa-plus']"
              @click="
                () => {
                  if (currentSubsection.needPayload) {
                    setPayloadItem(
                      currentSubsection.section,
                      currentSubsection.label,
                    )
                  }
                }
              "
            >
              <span v-if="currentSubsection.needPayload">
                New {{ newItemLabel }}
              </span>
              <span v-else>New</span>
              <template #right-icon>
                <i class="fa-solid fa-chevron-down" />
              </template>
            </BaseButton>
          </p>
        </div>
      </slot>
    </template>
    <div
      v-if="
        Object.keys(payloadItemInfo).length &&
        Object.keys(payloadItemInfo.payloadAttrs || {}).length
      "
      class="is-flex is-flex-direction-column px-4 pb-3"
      @click.stop
    >
      <div class="payload-fields">
        <div
          v-for="attrKey in Object.keys(payloadItemInfo.payloadAttrs)"
          :key="`payload-${attrKey}`"
        >
          <label class="payload-field-label has-text-weight-bold">
            New {{ payloadItemInfo.label }}
            {{ payloadItemInfo.payloadAttrs[attrKey].label }}
          </label>
          <FieldComponent
            :field-meta="payloadItemInfo.payloadAttrs[attrKey]"
            :data="payloadItemData[attrKey]"
            :is-edit-mode="true"
            style="width: 20rem"
            @edit="(value) => (payloadItemData[attrKey] = { value })"
            @keyup.enter="handleCreate"
          />
        </div>
      </div>
      <div class="is-flex is-justify-content-flex-end pt-3">
        <button
          class="button is-small is-outlined mr-2"
          @click.stop="
            () => {
              setPayloadItem(null)
              if (!showSections && !showBatchImport) $refs.dropdownRef?.close()
            }
          "
        >
          <span>Cancel</span>
        </button>
        <button
          class="button is-primary is-small"
          :class="{ 'is-outlined': !validPayload }"
          :disabled="!validPayload || isAddingNew"
          @click.stop="handleCreate"
        >
          <span class="icon">
            <i v-if="isAddingNew" class="fa-solid fa-spinner fa-spin" />
            <i v-else class="fa-solid fa-plus" />
          </span>
          <span>Create</span>
        </button>
      </div>
    </div>
    <template v-if="showSections || (showBatchImport && currentSubsection)">
      <template v-if="showSections">
        <div
          v-for="(sectionKey, sectionIndex) in [
            ...((currentSectionKey && [currentSectionKey]) || []),
            ...otherSectionsKeys,
          ]"
          :key="`label-${sectionKey}${sectionIndex}`"
          class="dd-section"
        >
          <div
            v-if="creatableSubsections[sectionKey]"
            class="dd-section-label is-clickable"
            @click.stop="
              openedDropdownSection =
                openedDropdownSection === sectionKey ? null : sectionKey
            "
          >
            <span class="icon">
              <i
                :class="
                  creatableSubsections[sectionKey] &&
                  creatableSubsections[sectionKey].iconClasses
                "
              />
            </span>
            {{ creatableSubsections[sectionKey].label }}
            <span
              class="icon dd-caret"
              :class="{ 'is-open': openedDropdownSection === sectionKey }"
            >
              <i class="fa-solid fa-caret-right" />
            </span>
          </div>
          <div
            v-if="creatableSubsections[sectionKey]"
            class="dd-section-items"
            :class="{ 'is-open': openedDropdownSection === sectionKey }"
          >
            <div class="dd-section-items-inner">
              <a
                v-for="(model, index) in creatableSubsections[sectionKey]
                  .models"
                :key="`new-${model.label}${index}`"
                class="dd-item"
                tabindex="0"
                @click.stop="
                  () => {
                    if (Object.keys(model.payloadAttrs).length > 0) {
                      setPayloadItem(sectionKey, model.label)
                    } else {
                      $router.push({ path: `/${model.url}/new` })
                    }
                  }
                "
              >
                {{ model.label }}
              </a>
            </div>
          </div>
        </div>
      </template>
      <div v-if="showBatchImport && currentSubsection" class="dd-section">
        <slot
          name="more-dropdown-content"
          :current-subsection-label="currentSubsection.label"
        />
      </div>
    </template>
  </DropdownButton>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { get } from 'lodash-es'
import { mapGetters } from 'vuex'

import { post } from '@/api'
import { hasPrivilege } from '@/auth/authorization'
import { useMenu } from '@/composables/useMenu'
import { formatModelLabel } from '@/utils'

const nonCreatableApps = []
const nonCreatableModelNames = ['workflowversion', 'publishedworkflow']
const nonCreatableModelTypes = ['total']
const modelNamesPayloadAttrs = {
  experiment: ['name', 'owned_by'],
  protocol: ['name', 'owned_by'],
}

export default {
  name: 'CreateItemButton',
  props: {
    appName: { type: String, default: '' },
    modelName: { type: String, default: '' },
    modelType: { type: String, default: '' },
    onlySections: { type: Array, default: () => [] },
    onlyModelNames: { type: Array, default: () => [] },
    oneClick: { type: Boolean, default: true },
    showSections: { type: Boolean, default: true },
    showBatchImport: { type: Boolean, default: false },
  },
  setup() {
    const v$ = useVuelidate()
    const { menuSections, currentSubsection: currentSubsec_ } = useMenu()
    return { v$, menuSections, currentSubsec_ }
  },
  data() {
    return {
      openedDropdownSection: null,
      payloadItemInfo: {},
      payloadItemData: {},
      isAddingNew: false,
    }
  },
  computed: {
    ...mapGetters('meta', ['getMeta']),
    ...mapGetters('meta', ['metaModelApps']),
    currentSubsection() {
      const sectionKey = this.currentSubsec_.section
      const subsectionKey = this.currentSubsec_.label

      const creatableFound =
        this.creatableSubsections[sectionKey]?.models?.find(
          (subs) => subs.label === subsectionKey,
        ) || {}
      const creatablePayload = creatableFound.payloadAttrs || {}
      return {
        ...this.currentSubsec_,
        creatable: Object.keys(creatableFound).length > 0,
        needPayload: Object.keys(creatablePayload).length > 0,
      }
    },
    newItemLabel() {
      return formatModelLabel(this.currentSubsec_, { plural: false })
    },
    acceptedSections() {
      let acceptedSections = Object.values(this.menuSections)
      const filterSections = this.onlySections.length >= 1
      const filterModelNames = this.onlyModelNames.length >= 1
      if (filterSections) {
        acceptedSections = acceptedSections.filter(([k]) =>
          this.onlySections.includes(k),
        )
      }
      if (filterModelNames) {
        acceptedSections = acceptedSections.reduce(
          (redSections, { key, ...section }) => {
            section.meta = section.models.filter(({ meta }) => {
              const modelName = meta[1]
              return this.onlyModelNames.includes(modelName)
            })
            return [
              ...redSections,
              [
                key,
                {
                  ...section,
                  meta: section.meta,
                },
              ],
            ]
          },
          [],
        )
      }
      return acceptedSections
    },
    creatableSubsections() {
      const creatable = this.acceptedSections.reduce(
        (enhancedSections, { key, ...sectionInfo }) => {
          const creatableModels = this.getCreatableModels(sectionInfo)
          if (creatableModels.length > 0) sectionInfo.models = creatableModels
          enhancedSections = { ...enhancedSections, [key]: sectionInfo }
          return enhancedSections
        },
        {},
      )
      return creatable
    },
    currentSection() {
      const sectionKey = this.currentSubsection?.section
      const subsectionInfo = this.creatableSubsections[sectionKey] || {}
      return (
        (sectionKey && {
          key: sectionKey,
          label: subsectionInfo.label,
          iconClasses: subsectionInfo?.iconClasses || [],
          models: subsectionInfo?.models || [],
        }) ||
        null
      )
    },

    currentSectionKey() {
      return this.currentSection?.key || null
    },
    otherSectionsKeys() {
      return Object.keys(this.creatableSubsections).filter(
        (s) => s !== this.currentSubsection.section,
      )
    },
    currentCreatable() {
      const creatableFound =
        this.creatableSubsections[this.currentSectionKey]?.models?.find(
          (subs) => subs.label === this.currentSubsection.label,
        ) || {}
      return creatableFound
    },
    canAdd() {
      if (!this.appName || !this.modelName) return true
      return hasPrivilege(this.appName, this.modelName, 'add')
    },
    showOneClickCreate() {
      return (
        this.oneClick &&
        this.currentSubsection.creatable &&
        !this.currentSubsection.needPayload
      )
    },
    validPayload() {
      return !this.v$.$invalid
    },
  },
  methods: {
    getPayloadAttrs(appName, modelName, modelType) {
      const meta = this.getMeta(appName, modelName, modelType) || {}
      const metaModelAttrs = meta?.model?.attributes || []
      const metaViewAttrs = meta?.view?.detail?.attributes || []
      const payloadAttrsKeys = modelNamesPayloadAttrs[modelName] || []
      return payloadAttrsKeys.reduce((payloadModels, k) => {
        const attrMetaModel = metaModelAttrs?.find((a) => a.name === k)
        const attrMetaView = metaViewAttrs?.find((a) => a.name === k)
        return {
          ...payloadModels,
          [k]: { ...attrMetaModel, ...attrMetaView },
        }
      }, {})
    },
    getCreatableModels(sectionInfo) {
      return sectionInfo?.models
        ?.filter((model) => {
          const [appName, modelName, modelType] = model.meta
          return (
            !nonCreatableApps.includes(appName) &&
            !nonCreatableModelNames.includes(modelName) &&
            !nonCreatableModelTypes.includes(modelType.toLowerCase()) &&
            hasPrivilege(appName, modelName, 'add') &&
            !get(this.metaModelApps, [appName, modelName, modelType])?.readonly
          )
        })
        ?.map((model) => {
          const [appName, modelName, modelType] = model.meta
          return {
            ...model,
            payloadAttrs: this.getPayloadAttrs(appName, modelName, modelType),
          }
        })
    },
    setPayloadItem(sectionKey, subsectionLabel) {
      const creatableFound =
        this.creatableSubsections[sectionKey]?.models?.find(
          (subs) => subs.label === subsectionLabel,
        ) || {}
      this.payloadItemInfo = creatableFound
      const payloadAttrs = this.payloadItemInfo?.payloadAttrs || {}
      this.payloadItemData = {}
      Object.keys(payloadAttrs).forEach((k) => {
        const defaultVal = payloadAttrs[k].default
        this.payloadItemData[k] = { value: defaultVal }
      })
    },
    handleCreate() {
      const path = `${this.payloadItemInfo.url}/new/`
      const payload = Object.entries(this.payloadItemData).reduce(
        (payload, [key, val]) => {
          return {
            ...payload,
            [key]: val.value?.id || val.value.name,
          }
        },
        {},
      )
      this.isAddingNew = true
      post(path, { data: payload })
        .then((res) => {
          const idNew = res.data.results.id.value
          const redirectPath = `/${this.payloadItemInfo.url}/${idNew}`
          this.$router.push({ path: redirectPath, query: { edit: 'true' } })
        })
        .catch((error) => {
          let message = error?.response?.data?.message || 'Server error.'
          this.$store.commit('notify', { type: 'error', message })
        })
        .finally(() => {
          this.isAddingNew = false
        })
    },
  },
  watch: {
    currentSectionKey(k) {
      this.openedDropdownSection = k
    },
    currentCreatable: {
      handler: function () {
        // initialize first payload item
        if (!this.showSections) {
          this.setPayloadItem(
            this.currentSectionKey,
            this.currentSubsection.label,
          )
        }
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.create-item-button {
  &:has(.trigger-content:empty) {
    display: none;
  }
}

.payload-fields {
  display: flex;
  flex-direction: column;
  row-gap: 0.75rem;
}

.payload-field-label {
  display: block;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.dd-section-items {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.2s ease-out;

  &.is-open {
    grid-template-rows: 1fr;
  }
}

.dd-section-items-inner {
  overflow: hidden;
  min-height: 0;
}

.dd-caret {
  transition: transform 0.2s ease-out;

  &.is-open {
    transform: rotate(90deg);
  }
}
</style>
